import type { GpuContext } from '../context.js';
import { workgroupCount } from '../context.js';
import type { GpuImage } from '../resources.js';
import { Struct } from '../uniform-writer.js';

/**
 * Separable box blur. `passes` > 1 stacks blurs, which converges on a Gaussian
 * (three passes is the usual approximation).
 *
 * `src` and `dst` must share size and format; `dst` may alias `src`.
 */
export function boxBlur(
  ctx: GpuContext,
  src: GpuImage,
  dst: GpuImage,
  radius: number,
  passes = 1,
): void {
  if (!dst.matches(src.width, src.height, src.format)) {
    throw new Error('[gpu-core] boxBlur requires matching size and format');
  }
  const r = Math.max(0, Math.round(radius));
  const temp = ctx.image(src.width, src.height, src.format, 'blur-temp');

  let input = src;
  for (let pass = 0; pass < Math.max(1, passes); pass++) {
    axisPass(ctx, input, temp, r, 0);
    axisPass(ctx, temp, dst, r, 1);
    input = dst;
  }
  temp.release();
}

function axisPass(ctx: GpuContext, src: GpuImage, dst: GpuImage, radius: number, axis: 0 | 1): void {
  ctx.dispatch(
    {
      module: 'blur.wgsl',
      defines: { DST_FORMAT: dst.format },
      bindings: [
        { kind: 'texture', format: src.format },
        { kind: 'storageTexture', format: dst.format },
        { kind: 'uniform' },
      ],
    },
    [src, dst, ctx.uniform(new Struct(16).i(0, src.width, src.height).i(8, radius, axis).bytes)],
    [workgroupCount(dst.width, 8), workgroupCount(dst.height, 8)],
    `boxBlur r=${radius} axis=${axis}`,
  );
}
